export function pad2(n: number) {
  return n < 10 ? `0${n}` : String(n)
}

export function toISODate(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

/**
 * Fuso da operação — Florianópolis = America/Sao_Paulo (UTC-3).
 *
 * `new Date().toISOString().slice(0, 10)` devolve a data em UTC. Como o
 * estabelecimento opera à noite, depois das 21:00 (Floripa) já é o dia seguinte
 * em UTC, e os formulários pré-preenchiam com a data ERRADA (amanhã). Os helpers
 * abaixo resolvem "hoje"/"mês atual" sempre no fuso da operação, independente do
 * fuso configurado no navegador.
 */
export const APP_TIMEZONE = 'America/Sao_Paulo'

const localDatePartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: APP_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** Data-calendário (YYYY-MM-DD) de um instante, no fuso da operação. */
export function toLocalISODate(date: Date = new Date()) {
  const parts = localDatePartsFormatter.formatToParts(date)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

/** "Hoje" (YYYY-MM-DD) no fuso da operação. */
export function todayLocalISO() {
  return toLocalISODate(new Date())
}

/** Mês corrente (YYYY-MM) no fuso da operação. */
export function currentMonthLocalISO() {
  return toLocalISODate(new Date()).slice(0, 7)
}

const localHourFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: APP_TIMEZONE,
  hour: '2-digit',
  hour12: false,
})

/** Hora local (0–23) de um instante no fuso da operação. */
export function localHour(date: Date = new Date()) {
  const raw = localHourFormatter.formatToParts(date).find((p) => p.type === 'hour')?.value ?? '0'
  // Alguns runtimes formatam meia-noite como "24" com hour12:false
  return Number(raw) % 24
}

export const DEFAULT_BUSINESS_DAY_CUTOFF_HOUR = 4

/**
 * Dia OPERACIONAL (YYYY-MM-DD) no fuso da operação.
 *
 * A pizzaria opera à noite (abre 16h–18h30, fecha 23h–01h): depois da
 * meia-noite a equipe ainda está fechando o dia de ONTEM. Até o corte
 * (default 4h; parametrizável por unidade via `business_day_cutoff_hour`)
 * o dia operacional é o dia-calendário anterior.
 *
 * Usar em DEFAULTS de telas operacionais (fechamento diário, expedição,
 * fechamento de motoboy). Nunca usar para reinterpretar uma data que o
 * usuário já escolheu explicitamente.
 */
export function businessDayLocalISO(
  cutoffHour: number = DEFAULT_BUSINESS_DAY_CUTOFF_HOUR,
  now: Date = new Date(),
) {
  if (localHour(now) < cutoffHour) {
    // -24h é seguro em America/Sao_Paulo (sem horário de verão desde 2019);
    // a formatação final é feita no fuso da operação.
    return toLocalISODate(new Date(now.getTime() - 24 * 60 * 60 * 1000))
  }
  return toLocalISODate(now)
}

export function parseISODate(value: string) {
  const m = /^\d{4}-\d{2}-\d{2}$/.exec(value)
  if (!m) return null
  const [y, mo, da] = value.split('-').map((n) => Number(n))
  const d = new Date(y, mo - 1, da)
  if (Number.isNaN(d.getTime())) return null
  if (d.getFullYear() !== y || d.getMonth() !== mo - 1 || d.getDate() !== da) return null
  return d
}

export function addMonths(date: Date, deltaMonths: number) {
  return new Date(date.getFullYear(), date.getMonth() + deltaMonths, 1)
}

export function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

export function endOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0)
}

export function startOfWeekMonday(date: Date) {
  const d = new Date(date)
  const day = d.getDay()
  const delta = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + delta)
  d.setHours(0, 0, 0, 0)
  return d
}

export function startOfWeek(date: Date) {
  const d = new Date(date)
  const day = d.getDay()
  d.setDate(d.getDate() - day)
  d.setHours(0, 0, 0, 0)
  return d
}

export function addDays(date: Date, days: number) {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

